import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Pencil, User, Calendar as CalendarIcon, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PhoneField } from "../../../components/PhoneField";
import ConfirmChannels, { type ConfirmChannel } from "./ConfirmChannels";
import BookedScreen from "./BookedScreen";
import type { CalendarLinks } from "../../../lib/calendarLinks";

type Errors = { name?: string; phone?: string; email?: string };

/** one of the salon's own WhatsApp / Telegram / MAX accounts from the create-response
 *  meta (inbox README §5.1): the client writes first and sends the code, which binds
 *  the chat to the booking */
export type PersonalLink = {
  channel: "whatsapp" | "telegram" | "max" | string;
  url: string | null;
  phone?: string | null;
  username?: string | null;
  code: string;
  text: string;
  /** she already chats with this account: nothing to write first (the receipt goes there at once) */
  bound?: boolean;
};

/** one entry of the server-driven messenger list (README §10.1), in the salon's order */
export type MessengerChannel = ConfirmChannel;

export type StatusPanel = {
  type: "success" | "error";
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  /** t.me deep link from the create-response meta — client chose messenger
   *  and is not connected yet (docs/codebase/messenger/README.md §4.1) */
  messengerLink?: string;
  /** "Sat, 3 October · 12:00 · Salon" — the booking just made (the draft is reset by then) */
  summary?: string;
  /** meta.personal_links: "write to us" on the salon's own accounts */
  personalLinks?: PersonalLink[];
  /** what the client picked on the review step, and the booking itself: read before
   *  the draft resets (BookedScreen.tsx) */
  confirmMethod?: 1 | 2;
  messengerChannel?: string;
  /** the picked channel confirms in the chat — how (bot | official | own) — and its custom
   *  name, if the salon gave one */
  messengerAuto?: boolean;
  messengerKind?: ConfirmChannel["kind"];
  messengerLabel?: string;
  details?: { service?: string; specialist?: string; when?: string };
  /** meta.calendar: the visit for the client's own calendar (google-calendar README §9.7) */
  calendar?: CalendarLinks;
};

export default function OverviewView({
  specialistName,
  specialistAvatar,
  services,
  dateLabel,
  timeLabel,
  onBack,
  onEditSpecialist,
  onEditServices,
  onEditDateTime,

  // Controlled contact props + handlers
  name,
  phone,
  email,
  comment,
  confirmMethod,
  messengersEnabled,
  messengerChannels,
  messengerChannel,
  onChangeMessengerChannel,
  smsOffered,
  smsConsent,
  onChangeSmsConsent,
  onChangeName,
  onChangePhone,
  onChangeEmail,
  onChangeComment,
  onChangeConfirmMethod,

  errors,
  onSubmit,
  isSubmitting,
  status,
  onOpenClientDashboard,
  phoneOffered = true,
}: {
  specialistName: string;
  specialistAvatar?: string;
  services: { id: string; name: string; price: string; duration: string; oldPrice?: string; lastHourPct?: number }[];
  dateLabel: string;
  timeLabel: string;
  onBack: () => void;
  onEditSpecialist: () => void;
  onEditServices: () => void;
  onEditDateTime: () => void;

  // NEW
  name: string;
  phone: string;
  email?: string;
  comment?: string;
  confirmMethod: 1 | 2;
  /** owner offers messengers at all (README §10.1); false → phone only */
  messengersEnabled?: boolean;
  /** deliverable channels, server-driven ("telegram" now, "whatsapp" at MSG6) */
  /** server-driven messenger list; `auto` = confirms right in the chat */
  messengerChannels?: MessengerChannel[];
  messengerChannel?: string;
  onChangeMessengerChannel?: (ch: string) => void;
  /** the salon texts through Boona Direct: offer the SMS consent tick */
  smsOffered?: boolean;
  smsConsent?: boolean;
  onChangeSmsConsent?: (on: boolean) => void;
  onChangeName: (v: string) => void;
  onChangePhone: (v: string) => void;
  onChangeEmail: (v: string) => void;
  onChangeComment: (v: string) => void;
  onChangeConfirmMethod: (m: 1 | 2) => void;

  errors?: Errors;
  onSubmit: () => void;
  isSubmitting?: boolean;
  status?: StatusPanel;
  onOpenClientDashboard?: () => void;
  /** the salon offers the phone call as a confirmation (Settings › Messenger) */
  phoneOffered?: boolean;
}) {
  const { t } = useTranslation();
  // Booked: the screen says what is left to do (BookedScreen.tsx) and hides the form.
  if (status?.type === "success") {
    return <BookedScreen status={status} onOpenClientDashboard={onOpenClientDashboard} />;
  }

  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-border/10 flex items-center gap-4">
            <Button variant="ghost" size="sm" className="p-2 h-auto rounded-full" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold">{t("widget.overview.title", "Review and confirm")}</h2>
          </div>

          {/* Scrollable body */}
          <div
            className="flex-1 overflow-y-auto p-6 space-y-4"
            style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
          >
            {/* Specialist panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 flex items-center gap-3">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={specialistAvatar} />
                <AvatarFallback><User className="w-5 h-5" /></AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{t("widget.common.specialist", "Specialist")}</div>
                <div className="font-medium leading-tight">{specialistName}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={onEditSpecialist}
                aria-label={t("widget.overview.edit_specialist", "Edit specialist")}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </div>

            {/* Date & time panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center ios-shadow shrink-0">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{t("widget.datetime.date_time", "Date and time")}</div>
                <div className="font-medium leading-tight">
                  {dateLabel} <span className="text-muted-foreground">•</span>{" "}
                  <Clock className="inline w-4 h-4 -mt-1 mr-1" />
                  {timeLabel}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={onEditDateTime}
                aria-label={t("widget.overview.edit_datetime", "Edit date and time")}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </div>

            {/* Services panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">{t("widget.services.title", "Services")}</div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={onEditServices}
                  aria-label={t("widget.overview.edit_services", "Edit services")}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
              <ul className="mt-2 space-y-2">
                {services.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 rounded-lg bg-background p-3 border">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.duration}</div>
                      {s.lastHourPct ? (
                        <div className="mt-0.5 inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                          {t("widget.last_hour.badge", "Last-minute −{{pct}}%", { pct: s.lastHourPct })}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-right">
                      {s.oldPrice ? (
                        <div className="text-xs text-muted-foreground line-through">{s.oldPrice}</div>
                      ) : null}
                      <div className={s.oldPrice ? "text-sm font-semibold text-emerald-700" : "text-sm font-semibold"}>{s.price}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-1 h-8 w-8 rounded-full"
                      onClick={onEditServices}
                      aria-label={t("widget.overview.edit_service", "Edit service {{name}}", { name: s.name })}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <Separator className="my-4" />

            {/* Contact details */}
            <div className="space-y-3">
              <div className="text-sm font-semibold">{t("widget.overview.contact_details", "Contact details")}</div>

              <div className="space-y-1.5">
                <Label className="flex gap-1 text-xs text-muted-foreground">
                  {t("widget.common.name", "Name")} <span className="text-destructive">*</span>
                </Label>
                <Input placeholder={t("widget.common.name_placeholder", "Your name")} value={name} onChange={(e) => onChangeName(e.target.value)} />
                {errors?.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="flex gap-1 text-xs text-muted-foreground">
                  {t("widget.common.phone", "Phone")} <span className="text-destructive">*</span>
                </Label>
                <PhoneField value={phone} onChange={onChangePhone} invalid={Boolean(errors?.phone)} />
                {errors?.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
                {smsOffered && (
                  <label className="mt-1 flex cursor-pointer items-start gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-primary"
                      checked={Boolean(smsConsent)}
                      onChange={(e) => onChangeSmsConsent?.(e.target.checked)}
                    />
                    <span>{t("widget.overview.sms_consent", "Text me about this booking (SMS confirmation and a reminder before the visit)")}</span>
                  </label>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("widget.common.email_optional", "Email (optional)")}</Label>
                <Input
                  type="email"
                  placeholder={t("widget.common.email_placeholder", "example@email.com")}
                  value={email ?? ""}
                  onChange={(e) => onChangeEmail(e.target.value)}
                  className={errors?.email ? "border-red-500" : ""}
                  aria-invalid={Boolean(errors?.email)}
                />
                <p className="text-xs text-muted-foreground/70 italic">{t("widget.common.email_hint", "Receive booking details and reminders by email.")}</p>
                {errors?.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("widget.overview.comment_optional", "Booking comment (optional)")}</Label>
                <Input placeholder={t("widget.overview.comment_placeholder", "Notes, preferences...")} value={comment ?? ""} onChange={(e) => onChangeComment(e.target.value)} />
              </div>

              <ConfirmChannels
                channels={messengersEnabled !== false ? messengerChannels ?? [] : []}
                phone={phoneOffered}
                confirmMethod={confirmMethod}
                messengerChannel={messengerChannel}
                onChangeConfirmMethod={onChangeConfirmMethod}
                onChangeMessengerChannel={(ch) => onChangeMessengerChannel?.(ch)}
              />
            </div>
          </div>

          {/* Sticky CTA / Status bar */}
          <div
            className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
          >
            <div className="p-4 pt-3">
              {/* STATUS PANEL (optional) */}
              {status && (
                <div
                  className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800"
                  role="alert"
                  aria-live="polite"
                >
                  <div>{status.message}</div>
                  {status.actionLabel && status.onAction && (
                    <div className="mt-2">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={status.onAction}>
                        {status.actionLabel}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!status && (
                <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-lg" onClick={onSubmit} disabled={isSubmitting}>
                  {isSubmitting ? t("widget.common.submitting", "Submitting...") : t("widget.overview.submit", "Book now")}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
